import type { components } from '@sportik/api-coach/schema';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { bodyRange, DEFAULT_BODY_RANGE } from '@/lib/body-range';
import { browserTimeZone, today } from '@/lib/dates';
import type { ViewRange } from '@/lib/view-range';
import { api } from '@/services/api-client';

type Metric = components['schemas']['MeasurementSeries']['metric'];

/**
 * The one definition of this read: one body metric over a range that ends
 * today, oldest first. The default is body weight over a year. A change of
 * metric or range keeps the series shown until the new one arrives.
 */
export function measurementsOptions(
  clientId: string,
  metric: Metric = 'body_weight',
  range: ViewRange = DEFAULT_BODY_RANGE,
) {
  return {
    ...api.queryOptions('get', '/clients/{clientId}/measurements', {
      params: {
        path: { clientId },
        query: {
          metric,
          ...bodyRange(range, today()),
          timeZone: browserTimeZone(),
        },
      },
    }),
    placeholderData: keepPreviousData,
  };
}

/** One body metric's recorded values, each with its change from the one before. */
export function useMeasurements(
  clientId: string,
  metric?: Metric,
  range?: ViewRange,
) {
  return useQuery(measurementsOptions(clientId, metric, range));
}

/** Several body metrics over the default range, one read each, in the order asked. */
export function useMeasurementsOf(clientId: string, metrics: Metric[]) {
  return useQueries({
    queries: metrics.map((metric) => measurementsOptions(clientId, metric)),
  });
}

/**
 * The one definition of this read: the progress photos the client shares,
 * oldest first, on one page.
 */
export function photosOptions(clientId: string) {
  return api.queryOptions('get', '/clients/{clientId}/photos', {
    params: {
      path: { clientId },
      query: { limit: 200, timeZone: browserTimeZone() },
    },
  });
}

/** The client's progress photos, each with the body weight of its day. */
export function usePhotos(clientId: string) {
  return useQuery(photosOptions(clientId));
}
